/**
 * useLeaderboardAgentTools — wires the Leaderboard page into FutureBot.
 *
 * Tool names must match functions/FutureBot/toolsets.js (crm-leaderboard).
 * get_leaderboard can switch the on-screen period; the coaching review tool
 * returns the VIEWED user's review — normally the current user, or the LO an
 * admin picked in the page's view-as selector (pageApi.viewAsUid, and the
 * page hands us a loadReview already bound to that user). Results carry a
 * viewingAs field whenever the admin is impersonating.
 */

import { useCallback, useMemo, useRef } from 'react';
import { waitForIdle } from '../../../components/FutureBot/agentToolHelpers';

const LEADERBOARD_SUGGESTIONS = [
  'How am I doing this month?',
  'Am I on pace for President\u2019s Club?',
  'How close am I to the $10M Club?',
  'Walk me through my monthly review',
];

const PERIODS = ['mtd', 'qtd', 'ytd', 'last_month', 'all_time'];
// Single calendar months ("2026-03") are also valid — the page offers every YTD month.
const MONTH_PERIOD_RE = /^20\d{2}-(0[1-9]|1[0-2])$/;

const isValidPeriod = (p) => PERIODS.includes(p) || MONTH_PERIOD_RE.test(p);

function trimRanking(r) {
  return {
    rank: r.rank,
    name: r.name,
    fundedVolume: r.fundedVolume,
    fundedUnits: r.fundedUnits,
    totalLeads: r.totalLeads,
    conversionPct: r.conversionPct,
    avgStlBiz: r.avgStlBiz,
    conversationPct: r.conversationPct,
    movement: r.movement,
  };
}

export function useLeaderboardAgentTools(pageApi, { enabled = true } = {}) {
  const apiRef = useRef(pageApi);
  apiRef.current = pageApi;
  const loadingRef = pageApi.lb.loadingRef;

  const tools = useMemo(() => ({
    get_leaderboard: async (args) => {
      const api = apiRef.current;
      const requested = args?.period && isValidPeriod(args.period) ? args.period : null;

      if (requested && requested !== api.lb.period) {
        api.lb.setPeriod(requested); // visible change; triggers a refetch
        await waitForIdle(loadingRef);
      }

      const { lb, uid, viewAsUid, viewAsName } = apiRef.current;
      const data = lb.data;
      if (!data) return { error: 'Leaderboard data has not loaded yet — try again in a moment.' };
      const meUid = viewAsUid || uid;
      const meRow = (data.rankings || []).find((r) => r.userId === meUid) || null;
      return {
        period: data.period,
        totals: data.totals,
        rankings: (data.rankings || []).slice(0, 30).map(trimRanking),
        me: meRow ? trimRanking(meRow) : null,
        viewingAs: meUid !== uid ? (viewAsName || 'another user') : undefined,
        weeklyWins: (data.weeklyWins || []).slice(0, 8),
      };
    },

    get_presidents_club_status: async () => {
      const data = apiRef.current.lb.data;
      if (!data?.presidentsClub) return { error: 'President\u2019s Club data has not loaded yet.' };
      const club = data.presidentsClub;
      const club10 = data.tenMillionClub || null;
      return {
        year: club.year,
        yearElapsedPct: club.yearElapsedPct,
        targets: club.targets,
        progress: (club.progress || []).slice(0, 20),
        // Second milestone: cross the YTD volume target, earn +bonusBps comp.
        tenMillionClub: club10
          ? {
            volumeTarget: club10.volumeTarget,
            bonusBps: club10.bonusBps,
            qualifiedCount: club10.qualifiedCount,
            progress: (club10.progress || []).slice(0, 20),
          }
          : null,
      };
    },

    get_my_coaching_review: async (args) => {
      const api = apiRef.current;
      let review = api.lb.review;
      if (!review || args?.generate === true) {
        // loadReview is bound by the page to the viewed user (view-as aware).
        const result = await api.lb.loadReview({ generate: args?.generate === true });
        if (!result.success) return { error: result.error };
        review = result.review;
      }
      const viewingAs = api.viewAsUid && api.viewAsUid !== api.uid ? (api.viewAsName || 'another user') : undefined;
      if (!review) {
        return {
          missing: true,
          viewingAs,
          note: 'No review exists for last month yet. Offer to generate it (call again with generate: true when the user agrees).',
        };
      }
      return {
        period: review.period,
        narrative: review.narrative,
        ownMetrics: review.metrics?.own || null,
        teamQuartiles: review.metrics?.team || null,
        private: true,
        viewingAs,
      };
    },
  }), [loadingRef]);

  const getContext = useCallback(async () => {
    const api = apiRef.current;
    const data = api.lb.data;
    const meUid = api.viewAsUid || api.uid;
    const impersonating = meUid !== api.uid;
    const me = (data?.rankings || []).find((r) => r.userId === meUid);
    const subject = impersonating ? `${api.viewAsName || 'The viewed user'} (admin view-as)` : 'Current user';
    return {
      referenceData: {},
      stateSummary: data
        ? `Leaderboard period: ${data.period}. ${data.totals?.participants ?? 0} LOs, ` +
          `$${Math.round((data.totals?.fundedVolume || 0) / 1e6)}M funded. ` +
          (me ? `${subject} is #${me.rank} with $${Math.round((me.fundedVolume || 0) / 1e3)}K funded.` : `${subject} has no production this period.`) +
          (impersonating ? ` Admin is viewing the page as ${api.viewAsName || 'another LO'}; treat that user as "me".` : '') +
          (api.lb.review ? ' A coaching review is loaded.' : ' No coaching review loaded yet.')
        : 'Leaderboard is still loading.',
    };
  }, []);

  const registration = useMemo(() => {
    if (!enabled) return null;
    return {
      pageId: 'crm-leaderboard',
      title: 'Leaderboard',
      tools,
      getContext,
      suggestions: LEADERBOARD_SUGGESTIONS,
    };
  }, [enabled, tools, getContext]);

  return { registration };
}

export default useLeaderboardAgentTools;
